import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { ApiClient } from '@finmin/api-client';
import { isApiError } from '@finmin/api-client';
import type { AuthUser } from './types';

/**
 * Window event fired by the api-client when token refresh fails and the
 * user must be logged out. AuthProvider subscribes to it to flip its React
 * state — keeping the redirect entirely client-side (no hard page reload,
 * which is what caused the iOS Safari reload-loop in production).
 */
export const AUTH_FAILURE_EVENT = 'finmin:auth-failure';

export interface AuthContextValue {
  user: AuthUser | null;
  status: 'loading' | 'authenticated' | 'unauthenticated';
  /** Refresh the user from the backend. Call after a successful login flow. */
  refresh: () => Promise<void>;
  /** Clear local tokens and reset state. Backend has no /logout endpoint — local only. */
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  client: ApiClient;
  children: ReactNode;
  /** Backend route to fetch the current user. */
  mePath?: string;
  /** Called on logout — host app wires this to clear the token store. */
  onClear?: () => void;
}

/**
 * Lightweight auth provider. The HEAVY lifting (token storage, refresh-on-401)
 * lives in the api-client; this component only owns the React-facing state:
 * who is logged in, are we still loading, and how do I log out.
 *
 * On mount, it tries `/api/v1/auth/me`. 200 → authenticated. 401 (after the
 * api-client's refresh interceptor failed to recover) → unauthenticated.
 *
 * NOTE: the FinMin backend has no /logout endpoint. Logout is purely local —
 * we drop the tokens and clear React-Query caches. The refresh token will
 * eventually expire server-side; if you need immediate revocation (e.g. for
 * compliance), add a backend endpoint and update `logout` to call it.
 */
export function AuthProvider({
  client,
  children,
  mePath = '/api/v1/auth/me',
  onClear,
}: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthContextValue['status']>('loading');

  const refresh = useCallback(async () => {
    try {
      const response = await client.get<AuthUser>(mePath);
      setUser(response.data);
      setStatus('authenticated');
    } catch (error) {
      if (isApiError(error) && error.status === 401) {
        setUser(null);
        setStatus('unauthenticated');
        return;
      }
      // Network or 5xx — surface but mark as unauthenticated so the app boots.
      setUser(null);
      setStatus('unauthenticated');
    }
  }, [client, mePath]);

  const logout = useCallback(() => {
    onClear?.();
    setUser(null);
    setStatus('unauthenticated');
  }, [onClear]);

  useEffect(() => {
    refresh().catch(() => setStatus('unauthenticated'));
  }, [refresh]);

  // Subscribe to the global auth-failure event dispatched by the api-client
  // when token refresh fails. Flip to 'unauthenticated' so the route guard
  // can do a soft SPA redirect to /login — no `window.location.assign`,
  // no hard reload, no iOS Safari reload flicker.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    function handleAuthFailure() {
      setUser(null);
      setStatus('unauthenticated');
    }
    window.addEventListener(AUTH_FAILURE_EVENT, handleAuthFailure);
    return () => window.removeEventListener(AUTH_FAILURE_EVENT, handleAuthFailure);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, status, refresh, logout }),
    [user, status, refresh, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
